import { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, Download, Search, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, Panel } from "@/components/Layout";
import { addressIssues } from "@/lib/addressCheck";
import { api, formatError } from "@/lib/api";

const EMPTY = { scope: "private", name: "", email: "", phone: "", line1: "", line2: "", line3: "", city: "", postcode: "" };
const FIELDS = [["name", "Full name", 2], ["email", "Email", 1], ["phone", "Mobile (optional)", 1], ["line1", "Address line 1", 2], ["line2", "Line 2 (optional)", 1], ["line3", "Line 3 (optional)", 1], ["city", "Town / City", 1], ["postcode", "Postcode", 1]];

const EntryForm = ({ entry, canOrg, onSaved, onClose }) => {
  const [f, setF] = useState(entry || EMPTY);
  const [busy, setBusy] = useState(false);
  const issues = addressIssues(f);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { id, issues: _i, can_edit: _c, updated_at: _u, ...body } = f;
      await (entry?.id ? api.put(`/address-book/${entry.id}`, body) : api.post("/address-book", body));
      toast.success("Saved to address book");
      onSaved();
    } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="grid sm:grid-cols-2 gap-3" data-testid="address-book-form">
      {canOrg && !entry?.id && (
        <select data-testid="address-book-scope-select" value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })} className="sm:col-span-2 h-10 rounded-md border border-input bg-background px-3 text-sm">
          <option value="private">My private address book</option><option value="org">Organisation address book (shared)</option>
        </select>
      )}
      {FIELDS.map(([k, label, span]) => (
        <label key={k} className={`space-y-1 ${span === 2 ? "sm:col-span-2" : ""}`}>
          <span className="text-xs font-semibold text-slate-500">{label}</span>
          <Input data-testid={`address-book-${k}-input`} value={f[k]} required={!label.includes("optional")} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-10" />
        </label>
      ))}
      {issues.length > 0 && <ul data-testid="address-book-form-issues" className="sm:col-span-2 ml-5 list-disc text-xs text-amber-700 dark:text-amber-300">{issues.map((i) => <li key={i}>{i}</li>)}</ul>}
      <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
        <button type="button" onClick={onClose} className="h-10 px-5 rounded-full border border-slate-300 dark:border-slate-700 text-sm">Cancel</button>
        <button data-testid="address-book-save-btn" disabled={busy} className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 disabled:opacity-60">{busy && <Loader2 size={14} className="animate-spin" />} Save</button>
      </div>
    </form>
  );
};

const exportCsv = async (scope) => {
  try {
    const { data } = await api.get("/address-book/export.csv", { params: { scope }, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
    Object.assign(document.createElement("a"), { href: url, download: `postalcloud-address-book-${scope}.csv` }).click();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error(formatError(e)); }
};

const Row = ({ e, onEdit, onDelete }) => (
  <li data-testid={`address-book-row-${e.id.slice(0, 8)}`} className="py-3 flex flex-wrap items-center justify-between gap-3">
    <div className="min-w-0">
      <p className="text-sm font-semibold">{e.name} {e.scope === "org" && <span className="ml-1 text-[10px] uppercase tracking-wider text-emerald-600">Organisation</span>}</p>
      <p className="text-xs text-slate-500 break-all">{e.email}</p>
      {e.phone && <p className="text-xs text-slate-500 whitespace-nowrap">{e.phone}</p>}
      <p className="text-xs text-slate-500">{[e.line1, e.line2, e.line3, e.city, e.postcode].filter(Boolean).join(", ")}</p>
      {e.issues.length > 0 && <p className="text-xs text-amber-700 dark:text-amber-300 flex gap-1 mt-1"><AlertTriangle size={12} className="mt-0.5" /> {e.issues[0]}</p>}
    </div>
    {e.can_edit && <div className="flex gap-2">
      <button data-testid={`address-book-edit-${e.id.slice(0, 8)}`} onClick={() => onEdit(e)} className="h-8 w-8 inline-flex items-center justify-center rounded-full border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"><Pencil size={13} /></button>
      <button data-testid={`address-book-delete-${e.id.slice(0, 8)}`} onClick={() => onDelete(e)} className="h-8 w-8 inline-flex items-center justify-center rounded-full border border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"><Trash2 size={13} /></button>
    </div>}
  </li>
);

export default function AddressBook() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("all");
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/address-book", { params: { q, scope } }).then((r) => setD(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [q, scope]); // eslint-disable-line react-hooks/exhaustive-deps
  const remove = async (e) => {
    if (!window.confirm(`Delete ${e.name} from the address book?`)) return;
    try { await api.delete(`/address-book/${e.id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(formatError(err)); }
  };
  return (
    <div data-testid="address-book-page" className="max-w-4xl">
      <PageHeader eyebrow="Recipients" title="Address book">
        <button data-testid="address-book-add-btn" onClick={() => setEditing({})} className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 transition-colors"><Plus size={15} /> Add recipient</button>
      </PageHeader>
      <Panel>
        <div className="flex flex-wrap gap-3 items-center">
          <label className="flex-1 min-w-[220px] flex items-center gap-2 h-10 rounded-full border border-slate-300 dark:border-slate-700 px-4"><Search size={14} className="text-slate-400" />
            <input data-testid="address-book-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, town or postcode" className="flex-1 bg-transparent text-sm outline-none min-w-0" /></label>
          {d?.has_org && <select data-testid="address-book-scope-filter" value={scope} onChange={(e) => setScope(e.target.value)} className="h-10 rounded-full border border-input bg-background px-4 text-sm">
            <option value="all">All</option><option value="private">Private</option><option value="org">Organisation</option></select>}
          <button data-testid="address-book-export-private" onClick={() => exportCsv("private")} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-slate-300 dark:border-slate-700 text-xs font-semibold hover:bg-slate-100 dark:hover:bg-slate-800"><Download size={13} /> Export private CSV</button>
          {d?.has_org && <button data-testid="address-book-export-org" onClick={() => exportCsv("org")} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-slate-300 dark:border-slate-700 text-xs font-semibold hover:bg-slate-100 dark:hover:bg-slate-800"><Download size={13} /> Export organisation CSV</button>}
        </div>
        <p className="text-xs text-slate-500 mt-3">Exports use the bulk-send CSV format. Add a <span className="font-mono">file</span> name for each letter and they're ready to upload.</p>
        {!d ? <Loader2 className="animate-spin text-slate-400 mt-6" size={18} /> : d.entries.length === 0
          ? <p data-testid="address-book-empty" className="text-sm text-slate-500 mt-6">No saved recipients yet. Add one here, or tick "Save this recipient" when you send a letter.</p>
          : <ul className="mt-4 divide-y divide-slate-100 dark:divide-slate-800">{d.entries.map((e) => <Row key={e.id} e={e} onEdit={setEditing} onDelete={remove} />)}</ul>}
      </Panel>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="address-book-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit recipient" : "Add recipient"}</DialogTitle></DialogHeader>
          {editing && <EntryForm entry={editing.id ? editing : null} canOrg={d?.can_edit_org} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
